import { useEffect, useState } from "react";
import { CheckCircle2, Clipboard, MessageCircle, Users } from "lucide-react";
import { PublicLayout } from "../../components/PublicLayout";
import { StatusTag } from "../../components/StatusTag";
import { api } from "../../lib/api";
import { money } from "../../lib/format";

interface InviteStatusItem {
  role: string;
  friend_account: string;
  line_nickname?: string;
  join_group_bonus: string;
  first_deposit_bonus: string;
  second_stage_bonus: string;
  third_deposit_bonus: string;
}

interface InviteStatusResult {
  member_account: string;
  items: InviteStatusItem[];
}

interface PublicLineSettings {
  line_guild_leader_url: string;
  line_official_url: string;
  line_group_checkin_url: string;
  invite_share_text: string;
  group_checkin_instruction_text: string;
}

const emptyLineSettings: PublicLineSettings = {
  line_guild_leader_url: "",
  line_official_url: "",
  line_group_checkin_url: "",
  invite_share_text: "",
  group_checkin_instruction_text: ""
};

const defaultInviteScript = [
  "我最近加入龍來喬公會，這邊福利多、活動也多，玩起來蠻有趣的~",
  "你幫我加一下這個 LINE，她邀你進群後，我這邊可以拿免費 6,666 點來玩",
  "你之後如果也想拿，也可以邀朋友加入，朋友進群你也可以領 6,666 點~",
  "快進來看看活動一起領優惠一起爆分贏錢吧 !"
].join("\n");

export function InvitePage() {
  const storedAccount = localStorage.getItem("current_member_account") ?? localStorage.getItem("memberAccount") ?? "";
  const storedIdentity = localStorage.getItem("current_identity_code") ?? localStorage.getItem("identity_code") ?? localStorage.getItem("phone_last3") ?? "";
  const [account, setAccount] = useState(storedAccount);
  const [identityCode, setIdentityCode] = useState(storedIdentity);
  const [status, setStatus] = useState<InviteStatusResult | null>(null);
  const [lineSettings, setLineSettings] = useState<PublicLineSettings>(emptyLineSettings);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api<PublicLineSettings>("/api/public/line-settings")
      .then((settings) => {
        if (!cancelled) setLineSettings(normalizeLineSettings(settings));
      })
      .catch(() => {
        if (!cancelled) setError("LINE 連結設定讀取失敗，請稍後再試。");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function copyText(text: string, successMessage: string, emptyMessage = "LINE 連結尚未設定，請聯繫客服。") {
    setMessage("");
    setError("");
    const value = text.trim();
    if (!value) {
      setError(emptyMessage);
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setMessage(successMessage);
    } catch {
      setError("複製失敗，請手動選取文字複製。");
    }
  }

  async function queryStatus() {
    setMessage("");
    setError("");
    setStatus(null);
    const trimmedAccount = account.trim();
    const trimmedIdentity = identityCode.trim();
    if (!trimmedAccount) {
      setError("請輸入會員帳號");
      return;
    }
    if (!trimmedIdentity) {
      setError("請輸入身份碼或手機後 3 碼");
      return;
    }

    setLoading(true);
    try {
      const result = await api<InviteStatusResult>("/api/public/invite-status", {
        method: "POST",
        body: JSON.stringify({ account: trimmedAccount, identity_code: trimmedIdentity })
      });
      setStatus(result);
      setMessage("邀友進度已更新。");
      localStorage.setItem("current_member_account", trimmedAccount);
      localStorage.setItem("memberAccount", trimmedAccount);
      localStorage.setItem("current_identity_code", trimmedIdentity);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查詢邀友進度失敗");
    } finally {
      setLoading(false);
    }
  }

  return (
    <PublicLayout title="邀好友" subtitle="邀請好友加入 LINE 群，獎勵由客服確認後派發">
      <section className="invite-hero">
        <span className="hero-kicker">Invite V1.1</span>
        <h1>邀好友加入龍來喬 LINE 群</h1>
        <div className="invite-status-line">
          <StatusTag label="LINE 群人工確認" tone="active" />
          <span>入群獎 {money(6666)}｜首存獎 {money(50000)}｜二存獎 {money(30000)}｜三存獎 {money(10000)}</span>
        </div>
        <p>
          先把龍來喬 LINE / 指定 LINE 群資訊傳給好友，好友加 LINE 後由客服協助入群、確認是否本線渠道與充值狀態。
          獎勵建立後會等待客服實際派發，不會自動入帳。
        </p>
      </section>

      <section className="invite-task-list">
        <article className="invite-task-card">
          <div className="invite-task-icon"><MessageCircle size={22} /></div>
          <div>
            <div className="quest-head">
              <h3>把 LINE 資訊傳給好友</h3>
              <StatusTag label="人工確認" tone="pending" />
            </div>
            <p>好友加入龍來喬 LINE / 指定 LINE 群後，請好友告訴客服你的會員帳號，客服會幫你確認邀友進度。</p>
            <div className="invite-result-panel">
              <button className="secondary-cta cta-line-action" type="button" onClick={() => copyText(lineSettings.line_guild_leader_url, "已複製公會長 LINE 連結。", "公會長 LINE 連結尚未設定，請聯繫客服。")}>
                <Clipboard size={16} /> 複製公會長 LINE 連結
              </button>
              <button className="secondary-cta cta-line-action" type="button" onClick={() => copyText(lineSettings.line_official_url, "已複製官方 LINE 連結。", "官方 LINE 連結尚未設定，請聯繫客服。")}>
                <MessageCircle size={16} /> 分享官方LINE給朋友
              </button>
              <button className="text-link-cta invite-copy-link" type="button" onClick={() => copyText(lineSettings.invite_share_text || defaultInviteScript, "已複製邀請說詞。")}>
                <Users size={16} /> 複製邀請說詞
              </button>
            </div>
          </div>
        </article>

        <article className="invite-task-card">
          <div className="invite-task-icon"><CheckCircle2 size={22} /></div>
          <div>
            <div className="quest-head">
              <h3>查詢邀友獎勵進度</h3>
              <StatusTag label={status ? "已查詢" : "輸入會員資料"} tone={status ? "active" : "pending"} />
            </div>
            <p>輸入會員帳號與身份碼，即可查看目前由客服確認的邀友進度與派發狀態。</p>
            <div className="invite-result-panel">
              <label>
                會員帳號
                <input value={account} onChange={(event) => setAccount(event.target.value)} placeholder="請輸入會員帳號" />
              </label>
              <label>
                身份碼 / 手機後 3 碼
                <input value={identityCode} onChange={(event) => setIdentityCode(event.target.value)} placeholder="手機後 3 碼" />
              </label>
              <button className="primary-cta" type="button" disabled={loading} onClick={queryStatus}>
                <CheckCircle2 size={16} /> {loading ? "查詢中..." : "查詢邀友進度"}
              </button>
            </div>
          </div>
        </article>
      </section>

      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}

      {status ? (
        <section className="invite-task-list">
          {status.items.length ? status.items.map((item, index) => (
            <article className="invite-task-card" key={`${item.friend_account}-${index}`}>
              <div className="invite-task-icon"><Users size={22} /></div>
              <div>
                <div className="quest-head">
                  <h3>好友進度</h3>
                  <StatusTag label="邀友獎勵進度" tone="active" />
                </div>
                <div className="summary-list">
                  <p>好友：{item.friend_account}</p>
                  {item.line_nickname ? <p>好友 LINE 暱稱：{item.line_nickname}</p> : null}
                  <p>入群獎 6,666：{item.join_group_bonus}</p>
                  <p>首存獎 50,000：{item.first_deposit_bonus}</p>
                  <p>二存獎 30,000：{item.second_stage_bonus}</p>
                  <p>三存獎 10,000：{item.third_deposit_bonus}</p>
                </div>
              </div>
            </article>
          )) : (
            <article className="invite-task-card">
              <div className="invite-task-icon"><Users size={22} /></div>
              <div>
                <h3>尚無邀友紀錄</h3>
                <p>若好友已加入 LINE，請確認好友是否已向客服提供你的會員帳號。</p>
              </div>
            </article>
          )}
        </section>
      ) : null}

      <section className="invite-actions">
        <a className="secondary-cta" href="/">返回大廳</a>
        <a className="primary-cta" href="/newbie">查看新人任務 <CheckCircle2 size={16} /></a>
      </section>
    </PublicLayout>
  );
}

function normalizeLineSettings(input: unknown): PublicLineSettings {
  if (!input || typeof input !== "object" || Array.isArray(input)) return emptyLineSettings;
  const row = input as Record<string, unknown>;
  return {
    line_guild_leader_url: String(row.line_guild_leader_url ?? ""),
    line_official_url: String(row.line_official_url ?? ""),
    line_group_checkin_url: String(row.line_group_checkin_url ?? ""),
    invite_share_text: String(row.invite_share_text ?? ""),
    group_checkin_instruction_text: String(row.group_checkin_instruction_text ?? "")
  };
}
